import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Maximize2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardPanel } from "@/components/building/DashboardPanel";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { PMEmptyState } from "@/domains/pm/components/shared";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useMessagesStore } from "@/stores/messages/hooks/useMessagesStore";
import { useStores } from "@/stores/useStores";
import { MessageBoard } from "./MessageBoard";
import { MessageBoardModal } from "./MessageBoardModal";
import { ChannelDropdown, type Channel } from "./ChannelDropdown";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface MessagesWidgetProps {
  buildingId: number;
  /** Active building scope (`building_projects.id`). When set, the board locks
   * to this project's channel and the channel picker is hidden. Undefined =
   * whole building ("All projects"), where the picker stays free. */
  projectId?: string;
}

const lastChannelKey = (buildingId: number) => `franklin:messages:lastChannel:${buildingId}`;

/**
 * Dashboard "Message board" — the left-hand column of the dashboard's
 * two-column section. The label row (on the white page) carries a red
 * unread-mention badge, the compact project-channel dropdown, and the expand
 * button that opens the full-screen board modal; the feed + composer sit
 * below it inside a white dashboard card. The feed itself is height-capped
 * and scrolls internally so a long conversation never stretches the section.
 * Dashboard-only (subcontractors are redirected off the dashboard) per v1
 * scope.
 */
export const MessagesWidget = observer(function MessagesWidget({
  buildingId,
  projectId,
}: MessagesWidgetProps) {
  const { t } = useLanguage();
  const { projects = [] } = useProjects(String(buildingId));
  const viewer = useDocumentViewer();
  const store = useMessagesStore();
  const currentUserId = useStores().userStore.userProfile?.id;
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const channels = useMemo<Channel[]>(
    () => projects.map((p) => ({ id: Number(p.id), name: p.name, status: p.status })),
    [projects]
  );

  // Project scope from the building-level scope selector. When set, the board
  // is pinned to this project's channel and the picker is hidden.
  const scopedChannelId = useMemo(() => {
    if (projectId == null) return null;
    const parsed = Number(projectId);
    return Number.isNaN(parsed) ? null : parsed;
  }, [projectId]);
  const isScoped = scopedChannelId != null;

  // Resolve the active channel. At project scope, lock to the scoped channel.
  // At whole-building scope: remembered → default → first. Re-runs if the
  // remembered channel disappears (project removed / access lost).
  useEffect(() => {
    if (channels.length === 0) {
      setSelectedId(null);
      return;
    }
    if (isScoped) {
      // Only pin once the scoped project is present in the loaded channels;
      // don't clobber the current selection while projects are still loading.
      if (channels.some((c) => c.id === scopedChannelId)) {
        setSelectedId(scopedChannelId);
      }
      return;
    }
    const stillValid = selectedId != null && channels.some((c) => c.id === selectedId);
    if (stillValid) return;

    const stored = Number(localStorage.getItem(lastChannelKey(buildingId)));
    if (stored && channels.some((c) => c.id === stored)) {
      setSelectedId(stored);
      return;
    }
    const def = projects.find((p) => p.is_default);
    setSelectedId(def ? Number(def.id) : channels[0].id);
  }, [channels, projects, selectedId, buildingId, isScoped, scopedChannelId]);

  // Scope the unread-mention badge to the dashboard's current scope. The store
  // keeps the count live via a building-wide realtime subscription (mentions in
  // non-selected channels included) plus refreshes on channel loads/mark-reads.
  useEffect(() => {
    if (!currentUserId) return;
    store.setUnreadContext(buildingId, currentUserId, scopedChannelId);
    return () => store.clearUnreadContext();
  }, [store, buildingId, currentUserId, scopedChannelId]);

  const handleSelect = (id: number) => {
    setSelectedId(id);
    localStorage.setItem(lastChannelKey(buildingId), String(id));
  };

  // At project scope the expanded board is limited to the scoped channel so the
  // full-screen view respects the same scope as the compact widget.
  const modalChannels = useMemo(
    () => (isScoped ? channels.filter((c) => c.id === scopedChannelId) : channels),
    [isScoped, scopedChannelId, channels]
  );

  const unread = store.unreadMentionCount;

  const labelRow = (
    <div className="flex items-center justify-between gap-2">
      <span className="flex min-w-0 items-center gap-2">
        <SettingsSectionLabel>{t.messages.title}</SettingsSectionLabel>
        {unread > 0 && (
          <span className="flex-shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
            {t.messages.unreadBadge.replace("{count}", String(unread))}
          </span>
        )}
      </span>
      {channels.length > 0 && (
        <div className="flex items-center gap-2">
          {!isScoped && (
            <ChannelDropdown channels={channels} selectedId={selectedId} onSelect={handleSelect} />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label={t.messages.expand}
            onClick={() => setExpanded(true)}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );

  if (channels.length === 0) {
    return (
      <div className="flex flex-col">
        {labelRow}
        <DashboardPanel className="mt-3 flex flex-1 flex-col">
          <PMEmptyState
            icon={MessageSquare}
            title={t.messages.noProjects}
            description={t.messages.noProjectsHint}
            className="min-h-[200px] flex-1"
          />
        </DashboardPanel>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        {labelRow}
        {/* Only one MessageBoard may be live at a time — they share the single
            MessagesStore (feed + realtime). While the modal is open the compact
            board is parked behind a placeholder so the modal owns the store.
            The feed area is capped (max-h) and scrolls internally — newest
            messages stay visible and long conversations never stretch the
            section — while the composer pins to the bottom. */}
        <DashboardPanel
          className="mt-3 flex max-h-[420px] min-h-[220px] flex-1 flex-col"
          {...pageInfoTarget("dashboard.messages")}
        >
          {selectedId != null &&
            (expanded ? (
              <div className="flex min-h-0 flex-1 items-center justify-center text-xs text-muted-foreground">
                {t.messages.title}
              </div>
            ) : (
              <MessageBoard
                buildingId={buildingId}
                buildingProjectId={selectedId}
                openViewer={viewer.openViewer}
                className="min-h-0 flex-1"
              />
            ))}
        </DashboardPanel>
      </div>

      <MessageBoardModal
        open={expanded}
        onOpenChange={setExpanded}
        channels={modalChannels}
        selectedId={selectedId}
        onSelect={handleSelect}
        buildingId={buildingId}
        openViewer={viewer.openViewer}
      />

      <DocumentViewerDialog
        document={viewer.document}
        blobUrl={viewer.blobUrl}
        blobData={viewer.blobData}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
      />
    </>
  );
});
